import { createLogger } from "@/logger";
import { NetworkMetricsService } from "@/services/perf/NetworkMetricsService";
import { PerfReporter } from "@/services/perf/PerfReporter";
import { RenderMetricsService } from "@/services/perf/RenderMetricsService";
import { OtelPerfSink } from "@/services/perf/sinks";

const log = createLogger("telemetry");

interface InitializePerfOptions {
  /** Mirrors the telemetry gate (`!inActiveTelemetry`): when false, perf collection stays off. */
  active: boolean;
  /** Origins/URLs that should bucket as first-party `api` calls in network stats. */
  apiHosts: string[];
  /** Resolves per-flush context (e.g. current workspaceId) attached to each rollup. */
  getContext?: () => Record<string, string | undefined>;
}

/**
 * Boots the perf-observability stack: render + network collectors and the periodic/lifecycle rollup reporter, which
 * ships one aggregated `perf.rollup` event per flush to Honeycomb, head-sampled under the `perf` category. Debug mode
 * ships the same rollups; its local detail is the flight recorder's. Returns the reporter so callers can stop it in
 * tests.
 *
 * Cost is gated end-to-end: nothing is observed, scheduled, or shipped unless telemetry is active
 * (`!inActiveTelemetry`), and rollup volume is bounded by the flush interval regardless of activity.
 */
export function initializePerf(options: InitializePerfOptions): PerfReporter | undefined {
  if (!options.active) return undefined;

  const sink = new OtelPerfSink();
  RenderMetricsService.setEnabled(sink.isActive);
  if (sink.isActive) NetworkMetricsService.start(options.apiHosts);

  const reporter = new PerfReporter(sink, { getContext: options.getContext });
  reporter.start();

  log.debug("[perf] initialized", { sink: sink.name });
  return reporter;
}
